import { ClientEndpointJsonVersion, ClientEndpointType, getClientEndpointJsonVersionDisplayName, JsonClientEndpoint } from "@app/shared";
import { useHref } from "react-router-dom";
import { useModel } from "@app/shared/src/hooks";
import { CheckboxInput, EnumSelect, Input, InputRow, NumericInput } from "@app/dashboard/src/components/shared/inputs";
import { apiDocsHrefForEndpoint, apiDocsPathForEndpoint } from "../EndpointApiDocs/specRegistry";
import { ClientEndpointEditorProps } from "./ClientEndpointEditor";

type EditJsonClientEndpointProps = {
} & ClientEndpointEditorProps;

export const EditJsonClientEndpoint: React.FC<EditJsonClientEndpointProps> = (props) => {
    const endpointWithDefaults = !!!props.endpoint?.id ? { ...props.endpoint, jsonVersion: 2 } : props.endpoint;
    const [endpoint, helper] = useModel(JsonClientEndpoint, endpointWithDefaults as JsonClientEndpoint);
    const onChange = (newEndpoint: Partial<JsonClientEndpoint>) => {
        props.onChange({ ...newEndpoint as JsonClientEndpoint, endpointType: ClientEndpointType.Json });
    };
    // Resolves the docs route under the app's basename (works behind the device proxy too), so the link can
    // open in a new tab without losing the dialog state. Version-aware: switching the selector below
    // repoints the link, because v1 and v2 are genuinely different wire contracts.
    //
    // useHref is called unconditionally to keep hook order stable, but its result is only used where the
    // host actually serves the docs page. WebConfig doesn't bundle it, so there the link is the absolute
    // dashboard URL the device reported and the basename-resolved path is discarded — and if the device
    // didn't report one, there's no usable link and the row is omitted entirely.
    const docsRelativeHref = useHref(
        apiDocsPathForEndpoint({ endpointType: ClientEndpointType.Json, jsonVersion: endpoint.jsonVersion }) ?? "/"
    );
    const docsHref = props.offlineMode
        ? apiDocsHrefForEndpoint(
              { endpointType: ClientEndpointType.Json, jsonVersion: endpoint.jsonVersion },
              { docsOrigin: props.docsOrigin, hostsDocsPage: false }
          )
        : docsRelativeHref;
    const versionLabel = endpoint.jsonVersion === ClientEndpointJsonVersion.V1 ? "v1" : "v2";
    return (
        <>
            {docsHref && (
                <InputRow>
                    <a href={docsHref} target="_blank" rel="noopener noreferrer" className="text-link-page underline text-sm">
                        View the JSON Push API documentation for {versionLabel} (payload schema, auth, delivery semantics) ↗
                    </a>
                </InputRow>
            )}
            <InputRow><Input label="Endpoint Name" {...helper.bindingsFor("endpointName", onChange)} /></InputRow>
            <InputRow><Input label="Endpoint URL" {...helper.bindingsFor("endpointUrl", onChange)} /></InputRow>
            <InputRow><EnumSelect enumType={ClientEndpointJsonVersion} nameToDisplayConverter={getClientEndpointJsonVersionDisplayName} {...helper.bindingsFor("jsonVersion", onChange)} /></InputRow>
            <InputRow><CheckboxInput label="Include Target History" {...helper.bindingsFor("includeTargetHistory", onChange)} /></InputRow>
            <InputRow><Input label="HMAC Secret" placeholder="HMAC shared secret key" {...helper.bindingsFor("sharedSecretKey", onChange)} /></InputRow>
            <InputRow><Input label="Custom Auth Header" placeholder="MY_AUTH_HEADER=authValue" {...helper.bindingsFor("customAuthHeader", onChange)} /></InputRow>
            <InputRow><CheckboxInput label="Enable Heartbeat" {...helper.bindingsFor("enableHeartbeat", onChange)} /></InputRow>
            {endpoint.enableHeartbeat &&
                <InputRow><NumericInput label="Seconds Between Heartbeat" {...helper.bindingsFor("secondsBetweenHearbeat", onChange)} /></InputRow>}
        </>
    );
}
